import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthResponse, PublicUser } from "@docmind/types";
import { apiFetch } from "./api.js";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; user: PublicUser }
  | { status: "anonymous" };

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  /**
   * The session token is in an httpOnly cookie, so JavaScript cannot read it
   * (that is the point -- W-19). "Am I signed in?" is therefore a question only
   * the server can answer, which is what this boot-time call is for.
   */
  useEffect(() => {
    const controller = new AbortController();

    apiFetch<PublicUser>("/api/me", { signal: controller.signal })
      .then((user) => setState({ status: "authenticated", user }))
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: "anonymous" });
      });

    return () => controller.abort();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });
    setState({ status: "authenticated", user: res.user });
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<AuthResponse>("/api/auth/register", {
      method: "POST",
      body: { email, password },
    });
    setState({ status: "authenticated", user: res.user });
  }, []);

  const logout = useCallback(async () => {
    // Clear locally even if the call fails -- the user asked to be signed out,
    // and leaving them looking signed in because the network blipped is worse
    // than a cookie that outlives the UI state.
    try {
      await apiFetch<void>("/api/auth/logout", { method: "POST" });
    } finally {
      setState({ status: "anonymous" });
    }
  }, []);

  const value = useMemo(
    () => ({ state, login, register, logout }),
    [state, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
